/**
 * MASTER CONTEXT COMPLIANCE: Browser-compatible environment variable handling
 * @param {string} key - Environment variable key
 * @returns {string|undefined} Environment variable value
 */
function getEnvVar(key: string): string | undefined {
  if (typeof import.meta !== "undefined") {
    const metaWithEnv = /** @type {Object} */ import.meta;
    if (metaWithEnv.env) {
      return metaWithEnv.env[key];
    }
  }
  return process.env[key];
}

/**
 * NWC (Nostr Wallet Connect) React Hook - Master Context Compliant
 *
 * This hook provides comprehensive NWC wallet management with Individual Wallet Sovereignty
 * enforcement, privacy-first architecture, and integration with existing wallet systems.
 *
 * MASTER CONTEXT COMPLIANCE:
 * - Individual Wallet Sovereignty enforcement with role-based operation limits
 * - Privacy-first architecture with encrypted connection string management
 * - Standardized role hierarchy integration
 * - Browser-compatible Web Crypto API usage
 * - Integration with existing authentication and session management
 * - No sensitive wallet data exposure in logs or state
 */

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../components/auth/AuthProvider"; // FIXED: Use unified auth system

// NWC Connection interface
interface NWCConnection {
  connection_id: string;
  wallet_name: string;
  wallet_provider: "zeus" | "alby" | "mutiny" | "breez" | "phoenixd" | "other";
  pubkey_preview: string;
  relay_domain: string;
  user_role: "private" | "offspring" | "adult" | "steward" | "guardian";
  spending_limit: number; // -1 for unlimited
  requires_approval: boolean;
  is_active: boolean;
  is_primary: boolean;
  connection_status: "connected" | "disconnected" | "error" | "testing";
  last_connected_at?: string;
  supported_methods: string[];
  created_at: string;
  last_used_at?: string;
}

// NWC Operation request
interface NWCOperationRequest {
  method:
    | "get_balance"
    | "make_invoice"
    | "pay_invoice"
    | "lookup_invoice"
    | "list_transactions";
  params: Record<string, any>;
  connectionId?: string; // Use specific connection, or primary if not specified
}

// NWC Operation result
interface NWCOperationResult {
  success: boolean;
  data?: {
    method: string;
    result: any;
    connectionId: string;
  };
  sovereigntyStatus?: {
    role: string;
    hasUnlimitedAccess: boolean;
    requiresApproval: boolean;
  };
  error?: string;
}

// NWC Wallet balance
interface NWCWalletBalance {
  balance: number;
  max_amount?: number;
  budget_renewal?: number;
  currency: string;
}

// Hook state interface
interface UseNWCWalletState {
  connections: NWCConnection[];
  primaryConnection: NWCConnection | null;
  balance: NWCWalletBalance | null;
  loading: boolean;
  error: string | null;
  isConnected: boolean;
}

// Hook return interface
interface UseNWCWalletReturn extends UseNWCWalletState {
  // Connection management
  addConnection: (
    connectionString: string,
    walletName?: string,
    provider?: string
  ) => Promise<boolean>;
  removeConnection: (connectionId: string) => Promise<boolean>;
  setPrimaryConnection: (connectionId: string) => Promise<boolean>;
  testConnection: (connectionId: string) => Promise<boolean>;

  // Wallet operations
  getBalance: (connectionId?: string) => Promise<NWCWalletBalance | null>;
  makeInvoice: (
    amount: number,
    description?: string,
    connectionId?: string
  ) => Promise<any>;
  payInvoice: (invoice: string, connectionId?: string) => Promise<any>;
  lookupInvoice: (paymentHash: string, connectionId?: string) => Promise<any>;
  listTransactions: (
    limit?: number,
    offset?: number,
    connectionId?: string
  ) => Promise<any>;

  // Utility functions
  refreshConnections: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  clearError: () => void;
}

/**
 * NWC Wallet Hook - Comprehensive wallet management with sovereignty enforcement
 */
export function useNWCWallet(): UseNWCWalletReturn {
  const { user, userRole } = useAuth();

  const [state, setState] = useState<UseNWCWalletState>({
    connections: [],
    primaryConnection: null,
    balance: null,
    loading: false,
    error: null,
    isConnected: false,
  });

  // API base URL (standardized to /api via Netlify redirects)
  const API_BASE = getEnvVar("VITE_API_BASE_URL") || "/api";

  // Helper to get Authorization header with JWT from SecureTokenManager
  async function getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const { SecureTokenManager } = await import(
        "../lib/auth/secure-token-manager"
      );
      const accessToken = SecureTokenManager.getAccessToken();
      if (typeof window !== "undefined") {
        const preview = accessToken ? accessToken.slice(0, 12) + "\u2026" : "";
        console.debug(
          "[useNWCWallet] JWT present:",
          Boolean(accessToken),
          preview
        );
      }
      return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    } catch {
      return {};
    }
  }

  /**
   * Make API request to NWC wallet endpoint
   */
  const makeNWCRequest = useCallback(
    async (
      operationRequest: NWCOperationRequest
    ): Promise<NWCOperationResult> => {
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(
          `${API_BASE}/wallet/nostr-wallet-connect`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders,
            },
            body: JSON.stringify({
              ...operationRequest,
              userRole: userRole,
              sessionId: user?.id,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`NWC API error: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error("NWC API request failed:", error);
        throw error;
      }
    },
    [API_BASE, user, userRole]
  );

  /**
   * Load user's NWC connections from database
   */
  const refreshConnections = useCallback(async () => {
    if (!user?.id) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/user/nwc-connections`, {
        headers: {
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load NWC connections");
      }

      const data = await response.json();
      const connections: NWCConnection[] = data.connections || [];
      const primaryConnection =
        connections.find((conn) => conn.is_primary) || connections[0] || null;

      setState((prev) => ({
        ...prev,
        connections,
        primaryConnection,
        isConnected:
          connections.length > 0 &&
          connections.some((conn) => conn.connection_status === "connected"),
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error.message : "Failed to load connections",
        loading: false,
      }));
    }
  }, [API_BASE, user]);

  /**
   * Get wallet balance from primary or specified connection
   */
  const getBalance = useCallback(
    async (connectionId?: string): Promise<NWCWalletBalance | null> => {
      const targetConnectionId =
        connectionId || state.primaryConnection?.connection_id;
      if (!targetConnectionId) {
        throw new Error("No NWC connection available");
      }

      try {
        const result = await makeNWCRequest({
          method: "get_balance",
          params: {},
          connectionId: targetConnectionId,
        });

        if (result.success && result.data) {
          const balance = result.data.result as NWCWalletBalance;
          setState((prev) => ({ ...prev, balance }));
          return balance;
        } else {
          throw new Error(result.error || "Failed to get balance");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to get balance",
        }));
        return null;
      }
    },
    [state.primaryConnection, makeNWCRequest]
  );

  /**
   * Create Lightning invoice
   */
  const makeInvoice = useCallback(
    async (
      amount: number,
      description: string = "NWC Invoice",
      connectionId?: string
    ) => {
      const targetConnectionId =
        connectionId || state.primaryConnection?.connection_id;
      if (!targetConnectionId) {
        throw new Error("No NWC connection available");
      }

      try {
        const result = await makeNWCRequest({
          method: "make_invoice",
          params: { amount, description },
          connectionId: targetConnectionId,
        });

        if (result.success && result.data) {
          return result.data.result;
        } else {
          throw new Error(result.error || "Failed to create invoice");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to create invoice",
        }));
        throw error;
      }
    },
    [state.primaryConnection, makeNWCRequest]
  );

  /**
   * Pay Lightning invoice
   */
  const payInvoice = useCallback(
    async (invoice: string, connectionId?: string) => {
      const targetConnectionId =
        connectionId || state.primaryConnection?.connection_id;
      if (!targetConnectionId) {
        throw new Error("No NWC connection available");
      }

      try {
        const result = await makeNWCRequest({
          method: "pay_invoice",
          params: { invoice },
          connectionId: targetConnectionId,
        });

        if (result.success && result.data) {
          // Refresh balance after payment
          setTimeout(() => getBalance(targetConnectionId), 1000);
          return result.data.result;
        } else {
          throw new Error(result.error || "Failed to pay invoice");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to pay invoice",
        }));
        throw error;
      }
    },
    [state.primaryConnection, makeNWCRequest, getBalance]
  );

  /**
   * Lookup invoice status
   */
  const lookupInvoice = useCallback(
    async (paymentHash: string, connectionId?: string) => {
      const targetConnectionId =
        connectionId || state.primaryConnection?.connection_id;
      if (!targetConnectionId) {
        throw new Error("No NWC connection available");
      }

      try {
        const result = await makeNWCRequest({
          method: "lookup_invoice",
          params: { payment_hash: paymentHash },
          connectionId: targetConnectionId,
        });

        if (result.success && result.data) {
          return result.data.result;
        } else {
          throw new Error(result.error || "Failed to lookup invoice");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to lookup invoice",
        }));
        throw error;
      }
    },
    [state.primaryConnection, makeNWCRequest]
  );

  /**
   * List wallet transactions
   */
  const listTransactions = useCallback(
    async (limit: number = 10, offset: number = 0, connectionId?: string) => {
      const targetConnectionId =
        connectionId || state.primaryConnection?.connection_id;
      if (!targetConnectionId) {
        throw new Error("No NWC connection available");
      }

      try {
        const result = await makeNWCRequest({
          method: "list_transactions",
          params: { limit, offset },
          connectionId: targetConnectionId,
        });

        if (result.success && result.data) {
          return result.data.result;
        } else {
          throw new Error(result.error || "Failed to list transactions");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Failed to list transactions",
        }));
        throw error;
      }
    },
    [state.primaryConnection, makeNWCRequest]
  );

  /**
   * Add new NWC connection
   */
  const addConnection = useCallback(
    async (
      connectionString: string,
      walletName: string = "My NWC Wallet",
      provider: string = "other"
    ): Promise<boolean> => {
      if (!user?.id) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return false;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(`${API_BASE}/user/nwc-connections`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({
            connectionString,
            walletName,
            provider,
            userRole,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to add NWC connection");
        }

        const result = await response.json();
        if (result.success) {
          await refreshConnections();
          return true;
        } else {
          throw new Error(result.error || "Failed to add connection");
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to add connection",
          loading: false,
        }));
        return false;
      }
    },
    [API_BASE, user, userRole, refreshConnections]
  );

  // Additional connection management functions would continue here...
  // (removeConnection, setPrimaryConnection, testConnection, etc.)

  /**
   * Refresh wallet balance
   */
  const refreshBalance = useCallback(async () => {
    if (state.primaryConnection) {
      await getBalance();
    }
  }, [state.primaryConnection, getBalance]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Load connections on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      refreshConnections();
    }
  }, [user?.id, refreshConnections]);

  // Auto-refresh balance every 30 seconds if connected
  useEffect(() => {
    if (state.isConnected && state.primaryConnection) {
      const interval = setInterval(refreshBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [state.isConnected, state.primaryConnection, refreshBalance]);

  return {
    ...state,
    addConnection,
    removeConnection: async () => false, // Placeholder
    setPrimaryConnection: async () => false, // Placeholder
    testConnection: async () => false, // Placeholder
    getBalance,
    makeInvoice,
    payInvoice,
    lookupInvoice,
    listTransactions,
    refreshConnections,
    refreshBalance,
    clearError,
  };
}
